import { Link, useLocation } from "react-router-dom";
import { usePortalLanguage } from "@/contexts/PortalLanguageContext";
import LaunchingSoon from "@/components/LaunchingSoon";

const LOGO_URL = "/fyrris-logo.png";

// ─── Column helper ────────────────────────────────────────────────────────────

const dispatchCloseMenu = () => window.dispatchEvent(new Event("closeMenu"));

function Column({
  title,
  items,
}: {
  title: string;
  items: { to: string; label: string }[];
}) {
  return (
    <nav aria-label={title}>
      <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
        {title}
      </h3>
      <ul className="space-y-3 text-sm">
        {items.map((it) => (
          <li key={it.to}>
            <Link
              to={it.to}
              className="text-gray-400 transition hover:text-white"
              onClick={dispatchCloseMenu}
            >
              {it.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

const Footer = () => {
  const lang = (usePortalLanguage?.() as any) || {};
  const t: (key: string, def?: string) => string =
    lang.t || ((key: string, def?: string) => def ?? key);

  const { pathname } = useLocation();

  // Locale detection — matches Navbar convention
  const locale =
    pathname.startsWith("/fr") ? "fr" :
    pathname.startsWith("/es") ? "es" : "en";

  // Locale-aware route map for translated paths
  const routes = {
    // es pointed at the English /retailers even though /es/minoristas exists.
    retailers:     { en: "/retailers",     fr: "/fr/detaillants",       es: "/es/minoristas"      },
    howItWorks:    { en: "/how-it-works",  fr: "/fr/comment-ca-marche", es: "/es/como-funciona"   },
    about:         { en: "/about",         fr: "/fr/a-propos",          es: "/es/sobre-nosotros"  },
    news:          { en: "/news",          fr: "/fr/nouvelles",         es: "/news"               },
    contact:       { en: "/contact",       fr: "/fr/contact",           es: "/es/contacto"        },
    privacy:       { en: "/privacy",       fr: "/fr/confidentialite",   es: "/privacy"            },
    terms:         { en: "/terms",         fr: "/fr/conditions",        es: "/es/terminos"        },
    manufacturers: { en: "/manufacturers", fr: "/fr/fabricants",        es: "/es/fabricantes"     },
    // /why-fyrris was a genuine orphan: routed since launch, linked from
    // nowhere, so neither crawlers nor visitors could reach it except by
    // guessing the URL. FR has its own pages; ES points at the English ones
    // because there is no Spanish version. Same fallback pattern already used
    // above for es -> /retailers and es -> /news.
    whyFyrris:     { en: "/why-fyrris",    fr: "/fr/pourquoi-fyrris",   es: "/why-fyrris"         },
    faq:           { en: "/faq",           fr: "/fr/faq",               es: "/faq"                },
    // Pricing and Book-a-Demo live in the Navbar's Retailers dropdown, which is
    // a Radix DropdownMenu: its content only mounts when opened, and its items
    // navigate via onClick rather than an href. So it contributes no crawlable
    // links at all, and these six routes (3 locales x 2 pages) were orphaned in
    // the prerendered HTML despite being in the sitemap. The footer is fully
    // server-rendered, so linking them here makes them reachable everywhere.
    retailersPricing: { en: "/retailers/pricing", fr: "/fr/detaillants/tarifs", es: "/es/minoristas/precios" },
    retailersDemo:    { en: "/retailers/demo",    fr: "/fr/detaillants/demo",   es: "/es/minoristas/demo"    },
  } as const;

  const lp = (key: keyof typeof routes) =>
    routes[key][locale] ?? routes[key].en;

  // ─── Column data ────────────────────────────────────────────────────────────

  const exploreCol = [
    { to: lp("howItWorks"),     label: t("footer.cols.company.howItWorks",        "How It Works")  },
    { to: lp("retailers"),      label: t("footer.cols.retailers.title",            "Retailers")     },
    { to: lp("retailersPricing"), label: t("footer.cols.retailers.pricing",        "Pricing")       },
    { to: lp("retailersDemo"),  label: t("footer.cols.retailers.bookDemo",        "Book a Demo")   },
    { to: lp("manufacturers"),  label: t("footer.cols.explore.manufacturers",      "Manufacturers") },
    { to: lp("whyFyrris"),      label: t("footer.cols.explore.whyFyrris",         "Why Fyrris")    },
    { to: lp("faq"),            label: t("footer.cols.explore.faq",               "FAQ")           },
    { to: lp("about"),          label: t("footer.cols.company.about",             "About")         },
    { to: lp("contact"),        label: t("footer.cols.company.contact",           "Contact")       },
  ];

  const legalCol = [
    { to: lp("privacy"), label: t("footer.cols.legal.privacy", "Privacy Policy")   },
    { to: lp("terms"),   label: t("footer.cols.legal.terms",   "Terms of Service") },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <footer
      role="contentinfo"
      className="w-full pt-16 bg-gray-950 text-gray-400 border-t border-gray-800"
    >
      <div className="max-w-5xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">

        {/* ── Col 1: Logo + tagline + badges ──────────────────────────────── */}
        <div className="flex flex-col gap-5">
          {/* es sent the logo to the English homepage, not /es. */}
          <Link to={locale === "fr" ? "/fr" : locale === "es" ? "/es" : "/"} onClick={dispatchCloseMenu}>
            <img
              src={LOGO_URL}
              alt="Fyrris"
              className="h-8 w-auto object-contain"
              loading="lazy"
            />
          </Link>

          <p className="text-sm leading-relaxed">
            <span className="font-semibold text-white">
              {t("footer.tagline", "Design Your Moments")}
            </span>
            <br />
            <span className="text-gray-500">
              {t(
                "footer.taglineLong",
                "Fyrris connects consumers, retailers, and manufacturers through immersive AR and geolocation."
              )}
            </span>
          </p>

          {/* Store badges are withheld until the apps are live. */}
          <div>
            <LaunchingSoon label={t("download.launchingSoon", "Launching soon")} />
          </div>
        </div>

        {/* ── Col 2-3: Link columns ────────────────────────────────────────── */}
        <Column
          title={t("footer.cols.explore.title", "Explore")}
          items={exploreCol}
        />
        <Column
          title={t("footer.cols.legal.title", "Legal")}
          items={legalCol}
        />
      </div>

      <hr className="border-gray-800" />

      <div className="max-w-5xl mx-auto px-6 py-5 flex justify-center text-xs text-gray-600">
        <p>
          Fyrris © {new Date().getFullYear()} · {t("footer.copyright", "All rights reserved.")}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
